import { Link, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import MediaSelector from '../../components/MediaSelector';
import DeleteModal from '../../components/DeleteModal';
import '../../styles/components/forms.css';

export default function IndividualActivity() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activity, setActivity] = useState(null);
    const [playlists, setPlaylists] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [audioPlayingNorwegian, setAudioPlayingNorwegian] = useState(null);
    const [audioPlayingEnglish, setAudioPlayingEnglish] = useState(null);
    const [isPlayingNorwegian, setIsPlayingNorwegian] = useState(false);
    const [isPlayingEnglish, setIsPlayingEnglish] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    
    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        activityName: '',
        shortDescription: '',
        description: '',
        voicingLevel: 'some',
        levelId: '',
        stepId: '',
        environmentId: '',
        isPublic: false,
        isFree: true,
        isLoop: false,
        minutes: '',
        audioUrl: '',
        videoUrl: '',
        tags: []
    });
    const [levels, setLevels] = useState([]);
    const [steps, setSteps] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [existingTags, setExistingTags] = useState([]);
    const [editTagInput, setEditTagInput] = useState('');
    const [editTagSuggestions, setEditTagSuggestions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchActivity();
        // Cleanup audio on unmount
        return () => {
            if (audioPlayingNorwegian) {
                audioPlayingNorwegian.pause();
            }
            if (audioPlayingEnglish) {
                audioPlayingEnglish.pause();
            }
        };
    }, [id]);

    const fetchDropdownData = async () => {
        try {
            const [levelsResponse, stepsResponse, environmentsResponse, tagsResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/levels`, { withCredentials: true }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/steps`, { withCredentials: true }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/environments`, { withCredentials: true }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/activities/tags`, { withCredentials: true })
            ]);
            setLevels(levelsResponse.data);
            setSteps(stepsResponse.data);
            setEnvironments(environmentsResponse.data);
            setExistingTags(tagsResponse.data);
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    };

    const handleEdit = () => {
        if (!activity) return;
        
        // Use full URL for preset so video/audio preview and MediaSelector work (including SAS tokens)
        // Backend returns norwegianURL / videoURL (capital URL); support both casings
        const audioUrl = activity.norwegianURL ?? activity.norwegianUrl ?? activity.englishURL ?? activity.englishUrl ?? '';
        const videoUrl = activity.videoURL ?? activity.videoUrl ?? '';
    
        setEditData({
            activityName: activity.name || '',
            shortDescription: activity.short_description || '',
            description: activity.description || '',
            voicingLevel: activity.voicing_level || 'some',
            levelId: activity.intensity_zone_idintensity_zone || '',
            stepId: activity.steps_idsteps || '',
            environmentId: activity.environment_idenvironment || '',
            isPublic: activity.isPublic === 1 || activity.isPublic === true,
            isFree: activity.isFree === 1 || activity.isFree === true,
            isLoop: activity.isLoop === 1 || activity.isLoop === true,
            minutes: activity.minutes || '',
            audioUrl,
            videoUrl,
            tags: activity.tags ? (typeof activity.tags === 'string' ? activity.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : activity.tags) : []
        });
        
        fetchDropdownData();
        setIsEditing(true);
        setError('');
        setSuccessMessage('');
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError('');
        setSuccessMessage('');
        setEditTagInput('');
        setEditTagSuggestions([]);
    };

    const handleEditTagInput = (e) => {
        const value = e.target.value;
        setEditTagInput(value);
        
        if (value.trim()) {
            const filtered = existingTags
                .filter(tag => tag.name.toLowerCase().includes(value.toLowerCase()))
                .map(tag => tag.name);
            setEditTagSuggestions(filtered);
        } else {
            setEditTagSuggestions([]);
        }
    };

    const addEditTag = (tagName) => {
        if (!editData.tags.includes(tagName) && tagName.trim()) {
            setEditData({
                ...editData,
                tags: [...editData.tags, tagName.trim()]
            });
        }
        setEditTagInput('');
        setEditTagSuggestions([]);
    };

    const removeEditTag = (tagToRemove) => {
        setEditData({
            ...editData,
            tags: editData.tags.filter(tag => tag !== tagToRemove)
        });
    };

    const handleDelete = async () => {
        if (!activity) return;
        
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`,
                { withCredentials: true }
            );
            
            if (response.status === 200) {
                setDeleteConfirm(null);
                // Navigate back to activities list after successful deletion
                navigate('/activities');
            }
        } catch (error) {
            console.error('Error deleting activity:', error);
            setError('Kunne ikke slette aktivitet. Vennligst prøv igjen.');
            setDeleteConfirm(null);
        }
    };

    const handleUpdate = async () => {
        // Validate that either audio or video file is selected
        if (!editData.audioUrl && !editData.videoUrl) {
            setError('Enten lydfil eller videofil er påkrevd for å oppdatere en aktivitet');
            return;
        }

        setSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`,
                {
                    name: editData.activityName,
                    shortDescription: editData.shortDescription,
                    description: editData.description,
                    voicingLevel: editData.voicingLevel,
                    idintensity_zone: editData.levelId,
                    idsteps: editData.stepId,
                    idenvironment: editData.environmentId || null,
                    isPublic: editData.isPublic ? 1 : 0,
                    isFree: editData.isFree ? 1 : 0,
                    isLoop: editData.isLoop ? 1 : 0,
                    minutes: editData.minutes,
                    norwegianUrl: editData.audioUrl,
                    englishUrl: editData.audioUrl,
                    videoUrl: editData.videoUrl,
                    tags: editData.tags || []
                },
                { withCredentials: true }
            );

            if (response.status === 200) {
                setSuccessMessage('Aktivitet oppdatert');
                setIsEditing(false);
                // Refresh activity data
                await fetchActivity();
                // Clear success message after 3 seconds
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (error) {
            console.error('Error updating activity:', error);
            setError('Kunne ikke oppdatere aktivitet. Vennligst prøv igjen.');
        } finally {
            setSaving(false);
        }
    };

    const fetchActivity = async () => {
        try {
            const [activityResponse, playlistsResponse] = await Promise.all([
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`,
                    { withCredentials: true }
                ),
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}/playlists`,
                    { withCredentials: true }
                )
            ]);
            
            setActivity(activityResponse.data);
            setPlaylists(playlistsResponse.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching activity:', error);
            setError('Kunne ikke laste aktivitet');
            setLoading(false);
        }
    };

    const handlePlayAudio = (url, language) => {
        if (language === "norwegian") {
            if (audioPlayingNorwegian && isPlayingNorwegian) {
                audioPlayingNorwegian.pause();
                setIsPlayingNorwegian(false);
            } else {
                if (audioPlayingNorwegian) {
                    audioPlayingNorwegian.play();
                } else {
                    const audio = new Audio(url);
                    audio.addEventListener('ended', () => setIsPlayingNorwegian(false));
                    audio.play();
                    setAudioPlayingNorwegian(audio);
                }
                setIsPlayingNorwegian(true);
            }
            // Pause English audio if playing
            if (audioPlayingEnglish && isPlayingEnglish) {
                audioPlayingEnglish.pause();
                setIsPlayingEnglish(false);
            }
        } else if (language === "english") {
            if (audioPlayingEnglish && isPlayingEnglish) {
                audioPlayingEnglish.pause();
                setIsPlayingEnglish(false);
            } else {
                if (audioPlayingEnglish) {
                    audioPlayingEnglish.play();
                } else {
                    const audio = new Audio(url);
                    audio.addEventListener('ended', () => setIsPlayingEnglish(false));
                    audio.play();
                    setAudioPlayingEnglish(audio);
                }
                setIsPlayingEnglish(true);
            }
            // Pause Norwegian audio if playing
            if (audioPlayingNorwegian && isPlayingNorwegian) {
                audioPlayingNorwegian.pause();
                setIsPlayingNorwegian(false);
            }
        }
    };

    if (loading) return <div className="loading">Laster...</div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!activity) return <div className="error-message">Aktivitet ikke funnet</div>;

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/activities" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til aktiviteter
                </Link>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <h1>{activity.name}</h1>
                    {!isEditing && (
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <button onClick={handleEdit} className="edit-header-button">
                                <i className="fas fa-edit"></i> Rediger
                            </button>
                            <button 
                                onClick={() => setDeleteConfirm(activity)} 
                                className="delete-header-button"
                            >
                                <i className="fas fa-trash-alt"></i> Slett
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {error && <div className="error-message" style={{ margin: '1rem 0', padding: '1rem', background: '#fee', color: '#c33', borderRadius: '4px' }}>{error}</div>}
            {successMessage && <div className="success-message" style={{ margin: '1rem 0', padding: '1rem', background: '#efe', color: '#3c3', borderRadius: '4px' }}>{successMessage}</div>}

            <div className="detail-card">
                {/* Main Activity Info */}
                <div className="activity-header">
                    <div className="activity-status">
                        {activity.isPublic ? (
                            <span className="status-badge published">
                                <i className="fas fa-globe"></i> Publisert
                            </span>
                        ) : (
                            <span className="status-badge private">
                                <i className="fas fa-lock"></i> Utkast
                            </span>
                        )}
                        {activity.isFree ? (
                            <span className="status-badge free">
                            </span>
                        ) : (
                            <span className="status-badge premium">
                                <i className="fas fa-star"></i> Premium
                            </span>
                        )}
                    </div>
                </div>

                {/* Activity Details */}
                <div className="activity-details">
                    {isEditing ? (
                        <>
                            {/* Edit Form */}
                            <div className="detail-section">
                                <h3>Rediger aktivitet</h3>
                                
                                <div className="form-group">
                                    <label>Aktivitetsnavn</label>
                                    <input
                                        type="text"
                                        value={editData.activityName}
                                        onChange={(e) => setEditData({ ...editData, activityName: e.target.value })}
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Kort beskrivelse</label>
                                    <textarea
                                        value={editData.shortDescription}
                                        onChange={(e) => setEditData({ ...editData, shortDescription: e.target.value })}
                                        className="form-textarea"
                                        rows="2"
                                        maxLength="150"
                                        placeholder="Kort beskrivelse (maks 150 tegn)"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Full beskrivelse</label>
                                    <textarea
                                        value={editData.description}
                                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                        className="form-textarea"
                                        rows="4"
                                        placeholder="Detaljert beskrivelse..."
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Intensitetsnivå</label>
                                        <select
                                            value={editData.levelId}
                                            onChange={(e) => setEditData({ ...editData, levelId: e.target.value })}
                                            className="form-select"
                                        >
                                            <option value="">Velg et nivå</option>
                                            {levels.map(level => (
                                                <option key={level.idintensity_zone} value={level.idintensity_zone}>
                                                    {level.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Steg</label>
                                        <select
                                            value={editData.stepId || ''}
                                            onChange={(e) => setEditData({ ...editData, stepId: e.target.value })}
                                            className="form-select"
                                        >
                                            <option value="">Velg et steg</option>
                                            {steps.map(step => (
                                                <option key={step.idsteps} value={step.idsteps}>
                                                    {step.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Miljø</label>
                                        <select
                                            value={editData.environmentId || ''}
                                            onChange={(e) => setEditData({ ...editData, environmentId: e.target.value })}
                                            className="form-select"
                                        >
                                            <option value="">Velg et miljø</option>
                                            {environments.map(env => (
                                                <option key={env.idenvironment} value={env.idenvironment}>
                                                    {env.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Varighet (minutter)</label>
                                        <input
                                            type="number"
                                            value={editData.minutes}
                                            onChange={(e) => setEditData({ ...editData, minutes: e.target.value })}
                                            className="form-input"
                                            min="1"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Stemmenivå</label>
                                    <select
                                        value={editData.voicingLevel}
                                        onChange={(e) => setEditData({ ...editData, voicingLevel: e.target.value })}
                                        className="form-select"
                                    >
                                        <option value="no">Ingen stemme</option>
                                        <option value="some">Litt stemme</option>
                                        <option value="consistent">Konsistent stemme</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Tagger</label>
                                    <div className="tags-input-container">
                                        <div className="tags-list">
                                            {editData.tags.map(tag => (
                                                <span key={tag} className="tag">
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeEditTag(tag)}
                                                        className="tag-remove"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <input
                                            type="text"
                                            value={editTagInput}
                                            onChange={handleEditTagInput}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addEditTag(editTagInput);
                                                }
                                            }}
                                            placeholder="Legg til tagger..."
                                            className="form-input"
                                        />
                                        {editTagSuggestions.length > 0 && (
                                            <div className="tag-suggestions">
                                                {editTagSuggestions.map(suggestion => (
                                                    <div
                                                        key={suggestion}
                                                        className="tag-suggestion"
                                                        onClick={() => addEditTag(suggestion)}
                                                    >
                                                        {suggestion}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-group toggle-group">
                                    <label className="toggle-label">
                                        <span className="toggle-text">Publisert aktivitet</span>
                                        <div className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={editData.isPublic}
                                                onChange={(e) => setEditData({ ...editData, isPublic: e.target.checked })}
                                            />
                                            <span className="toggle-slider"></span>
                                        </div>
                                    </label>

                                    <label className="toggle-label">
                                        <span className="toggle-text">Gratis aktivitet</span>
                                        <div className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={editData.isFree}
                                                onChange={(e) => setEditData({ ...editData, isFree: e.target.checked })}
                                            />
                                            <span className="toggle-slider"></span>
                                        </div>
                                    </label>

                                    <label className="toggle-label">
                                        <span className="toggle-text">Gjentakende aktivitet</span>
                                        <div className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={editData.isLoop}
                                                onChange={(e) => setEditData({ ...editData, isLoop: e.target.checked })}
                                            />
                                            <span className="toggle-slider"></span>
                                        </div>
                                    </label>
                                </div>

                                <div className="form-group">
                                    <label>Lydfil: <span className="required">*</span></label>
                                    {editData.audioUrl && (
                                        <div className="audio-preview-edit" style={{ marginBottom: '1rem' }}>
                                            <audio
                                                controls
                                                src={editData.audioUrl}
                                                style={{ maxWidth: '100%', borderRadius: '8px' }}
                                            >
                                                Nettleseren din støtter ikke lydspiller.
                                            </audio>
                                            <small className="text-muted" style={{ display: 'block', marginTop: '0.25rem' }}>
                                                Valgt lydfil
                                            </small>
                                        </div>
                                    )}
                                    <MediaSelector 
                                        onSelect={(url) => setEditData({ ...editData, audioUrl: url })}
                                        selectedUrl={editData.audioUrl}
                                    />
                                    {!editData.audioUrl && !editData.videoUrl && (
                                        <small className="error-text">Enten lydfil eller videofil er påkrevd</small>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Videofil: <span className="required">*</span></label>
                                    {editData.videoUrl && (
                                        <div className="video-container-activity" style={{ marginBottom: '1rem' }}>
                                            <video
                                                controls
                                                src={editData.videoUrl}
                                                className="activity-video"
                                                style={{ maxWidth: '100%', borderRadius: '8px' }}
                                            >
                                                Nettleseren din støtter ikke video-taggen.
                                            </video>
                                        </div>
                                    )}
                                    <MediaSelector 
                                        onSelect={(url) => setEditData({ ...editData, videoUrl: url })}
                                        selectedUrl={editData.videoUrl}
                                        folder="video"
                                        accept="video/*"
                                    />
                                    {!editData.audioUrl && !editData.videoUrl && (
                                        <small className="error-text">Enten lydfil eller videofil er påkrevd</small>
                                    )}
                                </div>

                                <div className="form-actions">
                                    <button 
                                        onClick={handleUpdate}
                                        disabled={saving || (!editData.audioUrl && !editData.videoUrl)}
                                        className="save-button"
                                    >
                                        {saving ? 'Lagrer...' : 'Lagre endringer'}
                                    </button>
                                    <button 
                                        onClick={handleCancel}
                                        disabled={saving}
                                        className="cancel-button"
                                    >
                                        Avbryt
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="detail-section">
                                <h3>Aktivitetsinformasjon</h3>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <i className="fas fa-clock"></i>
                                        <span>Varighet: {activity.minutes} minutter</span>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-running"></i>
                                        <span>Steg-nivå: {activity.stepsName}</span>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-mountain"></i>
                                        <span>Miljø: {activity.environmentName}</span>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-signal"></i>
                                        <span>Intensitets-sone: {activity.intensityName}</span>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-volume-up"></i>
                                        <span>Stemmenivå: {activity.voicing_level}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Description Section */}
                            <div className="detail-section">
                                <h3>Beskrivelse</h3>
                                <p className="description-text">{activity.description}</p>
                                {activity.short_description && (
                                    <p className="short-description">{activity.short_description}</p>
                                )}
                            </div>
                        </>
                    )}

                    {/* Audio Section */}
                    <div className="detail-section">
                        <h3>Lyd</h3>
                        <div className="audio-controls">
                            {(activity.norwegianURL || activity.norwegianUrl) && (
                                <div className="audio-item">
                                    <button 
                                        className={`audio-button norwegian ${isPlayingNorwegian ? 'playing' : ''}`}
                                        onClick={() => handlePlayAudio(activity.norwegianURL || activity.norwegianUrl, 'norwegian')}
                                    >
                                        <i className={`fas ${isPlayingNorwegian ? 'fa-pause' : 'fa-play'}`}></i>
                                        {isPlayingNorwegian ? 'Pause norsk versjon' : 'Spill norsk versjon'}
                                    </button>
                                </div>
                            )}
                            {(activity.englishURL || activity.englishUrl) && (
                                <div className="audio-item">
                                    <button 
                                        className={`audio-button english ${isPlayingEnglish ? 'playing' : ''}`}
                                        onClick={() => handlePlayAudio(activity.englishURL || activity.englishUrl, 'english')}
                                    >
                                        <i className={`fas ${isPlayingEnglish ? 'fa-pause' : 'fa-play'}`}></i>
                                        {isPlayingEnglish ? 'Pause engelsk versjon' : 'Spill engelsk versjon'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Video Section */}
                    {(activity.videoURL || activity.videoUrl) && (
                        <div className="detail-section">
                            <h3>Video</h3>
                            <div className="video-container">
                                <video 
                                    controls 
                                    src={activity.videoURL || activity.videoUrl}
                                    className="activity-video"
                                >
                                    Nettleseren din støtter ikke video-taggen.
                                </video>
                            </div>
                        </div>
                    )}

                    {/* Playlists Section */}
                    <div className="detail-section">
                        <h3>Tilknyttede spillelister</h3>
                        <div className="playlists-grid">
                            {playlists.length > 0 ? (
                                playlists.map(playlist => (
                                    <div key={playlist.idplaylists} className="playlist-card">
                                        <div className="playlist-info">
                                            <h4>{playlist.name}</h4>
                                            <span className="playlist-duration">
                                                <i className="fas fa-clock"></i> {playlist.duration} minutter
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="no-playlists">Ingen spillelister tilknyttet denne aktiviteten</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {deleteConfirm && (
                <DeleteModal 
                    isOpen={!!deleteConfirm}
                    onClose={() => setDeleteConfirm(null)}
                    onConfirm={handleDelete}
                    itemName={`aktiviteten "${deleteConfirm.name}"`}
                />
            )}

            <style jsx>{`
                .activity-header {
                    margin-bottom: 2rem;
                }

                .activity-status {
                    display: flex;
                    gap: 1rem;
                    margin-top: 1rem;
                }

                .status-badge {
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-size: 0.9rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .status-badge.published {
                    background-color: #e6f4ea;
                    color: #1e7e34;
                }

                .status-badge.private {
                    background-color: #fff3e0;
                    color: #e65100;
                }

                .status-badge.free {
                    background-color: #e3f2fd;
                    color: #1976d2;
                }

                .status-badge.premium {
                    background-color: #fdf2e9;
                    color: #c17d10;
                }

                .detail-section {
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .detail-section h3 {
                    color: #2c3e50;
                    margin-bottom: 1rem;
                    padding-bottom: 0.5rem;
                    border-bottom: 2px solid #eee;
                }

                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                }

                .info-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem;
                    background: #f8f9fa;
                    border-radius: 6px;
                }

                .info-item i {
                    color: #666;
                    font-size: 1.1rem;
                }

                .description-text {
                    font-size: 1.1rem;
                    line-height: 1.6;
                    color: #2c3e50;
                }

                .short-description {
                    margin-top: 1rem;
                    font-size: 0.95rem;
                    color: #666;
                    font-style: italic;
                }

                .audio-controls {
                    display: flex;
                    gap: 1rem;
                    flex-wrap: wrap;
                }

                .audio-button {
                    padding: 0.75rem 1.5rem;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.95rem;
                    transition: all 0.2s;
                }

                .audio-button.norwegian {
                    background-color: #e3f2fd;
                    color: #1976d2;
                }

                .audio-button.english {
                    background-color: #e8f5e9;
                    color: #2e7d32;
                }

                .audio-button.playing {
                    background-color: #f5f5f5;
                }

                .audio-button:hover {
                    filter: brightness(0.95);
                }

                .video-container-activity {
                    width: 100%;
                    max-width: 800px;
                    margin: 0 auto;
                }

                .activity-video {
                    width: 100%;
                    border-radius: 8px;
                }

                .playlists-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                    gap: 1rem;
                }

                .playlist-card {
                    background: #f8f9fa;
                    padding: 1rem;
                    border-radius: 6px;
                    border: 1px solid #eee;
                    transition: transform 0.2s;
                }

                .playlist-card:hover {
                    transform: translateY(-2px);
                }

                .playlist-info h4 {
                    margin: 0 0 0.5rem 0;
                    color: #2c3e50;
                }

                .playlist-duration {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #666;
                    font-size: 0.9rem;
                }

                .no-playlists {
                    color: #666;
                    font-style: italic;
                    text-align: center;
                    padding: 1rem;
                }

                .edit-header-button {
                    padding: 0.75rem 1.5rem;
                    background-color: #007bff;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.95rem;
                    transition: all 0.2s;
                }

                .edit-header-button:hover {
                    background-color: #0056b3;
                }

                .delete-header-button {
                    padding: 0.75rem 1.5rem;
                    background-color: #dc3545;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.95rem;
                    transition: all 0.2s;
                }

                .delete-header-button:hover {
                    background-color: #c82333;
                }

                .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 0.5rem;
                    font-weight: 600;
                    color: #2c3e50;
                }

                .form-group .required {
                    color: #dc3545;
                }

                .form-input,
                .form-select,
                .form-textarea {
                    width: 100%;
                    padding: 0.75rem;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 1rem;
                    font-family: inherit;
                }

                .form-textarea {
                    resize: vertical;
                    min-height: 100px;
                }

                .form-row {
                    display: flex;
                    flex-direction: row;
                    gap: 1rem;
                }

                .toggle-group {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .toggle-label {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                }

                .toggle-text {
                    font-weight: 500;
                }

                .toggle-switch {
                    position: relative;
                    width: 50px;
                    height: 24px;
                }

                .toggle-switch input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }

                .toggle-slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: #ccc;
                    transition: 0.4s;
                    border-radius: 24px;
                }

                .toggle-slider:before {
                    position: absolute;
                    content: "";
                    height: 18px;
                    width: 18px;
                    left: 3px;
                    bottom: 3px;
                    background-color: white;
                    transition: 0.4s;
                    border-radius: 50%;
                }

                .toggle-switch input:checked + .toggle-slider {
                    background-color: #007bff;
                }

                .toggle-switch input:checked + .toggle-slider:before {
                    transform: translateX(26px);
                }

                .form-actions {
                    display: flex;
                    flex-direction: row;
                    gap: 1rem;
                    margin-top: 2rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid #eee;
                    width: 100%;
                }

                .save-button {
                    padding: 0.75rem 2rem;
                    background-color: #28a745;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 1rem;
                    font-weight: 600;
                    transition: all 0.2s;
                    width: 100%;
                }

                .save-button:hover:not(:disabled) {
                    background-color: #218838;
                }

                .save-button:disabled {
                    background-color: #ccc;
                    cursor: not-allowed;
                }

                .cancel-button {
                    padding: 0.75rem 2rem;
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 1rem;
                    font-weight: 600;
                    transition: all 0.2s;
                    width: 100%;
                }

                .cancel-button:hover:not(:disabled) {
                    background-color: #5a6268;
                }

                .cancel-button:disabled {
                    background-color: #ccc;
                    cursor: not-allowed;
                }

                .error-text {
                    color: #dc3545;
                    font-size: 0.875rem;
                    margin-top: 0.25rem;
                    display: block;
                }

                @media (max-width: 768px) {
                    .form-row {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
}